import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import LegalDocument from './LegalDocument';
import privacy from './privacy.md?raw';
import terms from './terms.md?raw';

/**
 * `/terms` and `/privacy` (D7). Both readable signed out, both opened in a new
 * tab from the sign-up card's checkbox.
 *
 * The site's own ground and type at a readable measure — long documents are
 * read, not skimmed, and 68 characters is about as wide as a line can be
 * before the eye loses its place coming back.
 */

const DOCUMENTS = {
  terms: { source: terms, title: 'Terms of Use' },
  privacy: { source: privacy, title: 'Privacy Policy' },
} as const;

const Page = styled('div')({
  position: 'fixed',
  inset: 0,
  overflowY: 'auto',
  background: BRAND.mocha,
  color: BRAND.parchment,
});

const Top = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  maxWidth: '68ch',
  margin: '0 auto',
  padding: '24px 20px 4px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: { padding: '16px 18px 4px' },
}));

const Home = styled(Link)(({ theme }) => ({
  display: 'block',
  width: 190,
  color: BRAND.parchment,
  '&:hover': { opacity: 0.8 },
  [theme.breakpoints.down('sm')]: { width: 160 },
}));

const TopLink = styled(Link)({
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 14,
  color: BRAND.parchment,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { opacity: 0.75 },
});

const Column = styled('main')(({ theme }) => ({
  maxWidth: '68ch',
  margin: '0 auto',
  padding: '20px 20px 120px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: { padding: '14px 18px 90px' },
}));

export default function LegalPage({ document: which }: { document: keyof typeof DOCUMENTS }) {
  const { source, title } = DOCUMENTS[which];

  /**
   * A link to `#7` from the other tab, or from inside the document, has to land
   * on the section rather than at the top: the page mounts after the browser
   * has already tried and failed to find the anchor.
   */
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    window.requestAnimationFrame(() => {
      window.document.getElementById(id)?.scrollIntoView();
    });
  }, [which]);

  return (
    <Page>
      <title>{`${title} · CommonGround`}</title>

      <Top>
        <Home to="/" aria-label="CommonGround — home">
          <img src={`${import.meta.env.BASE_URL}commonground-logo.svg`} alt="" style={{ display: 'block', width: '100%' }} />
        </Home>
        <TopLink to={which === 'terms' ? '/privacy' : '/terms'}>
          {which === 'terms' ? 'Privacy Policy' : 'Terms of Use'} →
        </TopLink>
      </Top>

      <Column>
        <LegalDocument markdown={source} />
      </Column>
    </Page>
  );
}
